import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Camera, ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchAttachments } from '@/services/api';

export function PhotoViewerButton({ module, objectid, hasFoto, className = '' }) {
  const [open, setOpen] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);

  const { data: photos = [], isLoading } = useQuery({
    queryKey: ['attachments', module, objectid],
    queryFn: () => fetchAttachments(module, objectid),
    enabled: open && !!objectid,
    staleTime: 300000,
  });

  // Only show if we explicitly know there's a photo, or if hasFoto is undefined (backward compat)
  if (!objectid) return null;
  if (hasFoto === false) return null;

  const prev = () => setCurrentIdx(i => Math.max(0, i - 1));
  const next = () => setCurrentIdx(i => Math.min(photos.length - 1, i + 1));

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className={`h-6 gap-1 text-[10px] px-2 ${className}`}
        style={{
          color: 'var(--vz-blue)',
          border: '1px solid rgba(74,158,255,0.2)',
          background: 'rgba(74,158,255,0.06)',
        }}
        onClick={(e) => { e.stopPropagation(); setOpen(true); setCurrentIdx(0); }}
        data-testid="photo-viewer-button"
      >
        <Camera size={11} />
        Fotos
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl p-0 overflow-hidden"
          style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}
        >
          <DialogHeader className="px-5 pt-4 pb-3 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
            <DialogTitle className="font-montserrat text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--vz-text-primary)' }}>
              <Camera size={14} style={{ color: 'var(--vz-blue)' }} />
              Fotos del registro
              {photos.length > 0 && (
                <Badge className="text-[10px] ml-1" style={{ background: 'rgba(74,158,255,0.15)', color: 'var(--vz-blue)', border: '1px solid rgba(74,158,255,0.3)' }}>
                  {photos.length} foto{photos.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="p-5">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 size={24} className="animate-spin" style={{ color: 'var(--vz-blue)' }} />
                <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Cargando fotos...</p>
              </div>
            ) : photos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Camera size={28} style={{ color: 'var(--vz-text-muted)', opacity: 0.4 }} />
                <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>No hay fotos adjuntas a este registro</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Main photo */}
                <div
                  className="relative rounded-xl overflow-hidden flex items-center justify-center"
                  style={{ background: 'var(--vz-bg-elevated)', minHeight: 320, maxHeight: 480 }}
                >
                  <img
                    src={photos[currentIdx]?.url}
                    alt={photos[currentIdx]?.name || 'Foto'}
                    className="max-w-full max-h-[460px] object-contain"
                    onError={(e) => { e.target.src = ''; e.target.alt = 'Error al cargar imagen'; }}
                  />
                  {photos.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full"
                        style={{ background: 'rgba(0,0,0,0.5)', color: '#fff' }}
                        onClick={prev}
                        disabled={currentIdx === 0}
                      >
                        <ChevronLeft size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full"
                        style={{ background: 'rgba(0,0,0,0.5)', color: '#fff' }}
                        onClick={next}
                        disabled={currentIdx === photos.length - 1}
                      >
                        <ChevronRight size={16} />
                      </Button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 rounded-full px-3 py-1">
                        <span className="text-[11px] text-white">{currentIdx + 1} / {photos.length}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Thumbnails */}
                {photos.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {photos.map((photo, idx) => (
                      <button
                        key={photo.id}
                        onClick={() => setCurrentIdx(idx)}
                        className="flex-shrink-0 rounded-lg overflow-hidden transition-all duration-150"
                        style={{
                          width: 60, height: 48,
                          border: `2px solid ${idx === currentIdx ? 'var(--vz-blue)' : 'var(--vz-border-subtle)'}`,
                          opacity: idx === currentIdx ? 1 : 0.6,
                        }}
                      >
                        <img src={photo.url} alt={photo.name} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                  {photos[currentIdx]?.name} — {photos[currentIdx]?.size ? `${Math.round(photos[currentIdx].size / 1024)} KB` : ''}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PhotoViewerButton;
